import React, { memo, useState, useMemo } from 'react';
import { arrayOf, string, shape, oneOfType, object } from 'prop-types';
import format from 'date-fns/format';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import LoaderBar from 'components/LoaderBar';
import Checkbox from 'components/Checkbox';
import Dropdown from 'components/Dropdown';
import SearchInput from 'components/SearchInput';
import DropdownWithIcon from 'components/DropdownWithIcon';
import ExportModal from 'components/ExportModal';
import { ReactComponent as ExportIcon } from 'assets/icons/file-download.svg';
import Pagination from 'components/Pagination';
import breakpoints from 'utils/breakpoints';
import PATHS from 'routes/paths';

import { ReactComponent as ReceiptIcon } from 'assets/icons/receipt.svg';
import { orderList as TEXT } from 'locales/en';
import { useMediaQuery } from 'hooks';
import {
  getOrderList,
  acceptOrder,
  rejectOrder,
  completeOrder,
  sendReceipt,
  setOrderPage,
} from 'redux/slices/order';
import {
  ACCEPT,
  REJECT,
  COMPLETE,
  SEND_RECEIPT,
  ORDER_STATUS_OPTIONS,
  ORDER_DATE_OPTIONS,
} from './filters';
import { PENDING, REJECTED, SHIPPED } from 'utils/transform';

const OrderTable = ({
  headers,
  data,
  onChangeStatusFilter,
  onSearch,
  onChangeDateFilter,
  onConfirmExport,
  displayLoader,
  pagination,
  searchTerm,
  statusFilter,
  dateFilter,
}) => {
  const history = useHistory();
  const [selectedAction, setSelectedAction] = useState(undefined);
  const [checkedItem, setCheckedItem] = useState({});
  const isLarge = useMediaQuery(breakpoints.lg);

  const dispatch = useDispatch();

  const onPageChange = async ({ selected }) => {
    await dispatch(setOrderPage(selected + 1));
    dispatch(getOrderList()).then(() => window.scrollTo(0, 0));
  };

  const onApplyAction = (action) => {
    switch (action.value) {
      case ACCEPT.value:
        dispatch(acceptOrder(checkedItem.id)).then(() => {
          setCheckedItem({});
          setSelectedAction(undefined);
        });
        break;
      case REJECT.value:
        dispatch(rejectOrder(checkedItem.id)).then(() => {
          setCheckedItem({});
          setSelectedAction(undefined);
        });
        break;

      case COMPLETE.value:
        dispatch(completeOrder(checkedItem.id)).then(() => {
          setCheckedItem({});
          setSelectedAction(undefined);
        });
        break;

      case SEND_RECEIPT.value:
        dispatch(sendReceipt(checkedItem.id)).then(() => {
          setCheckedItem({});
          setSelectedAction(undefined);
        });
        break;

      default:
        break;
    }
  };

  const ORDER_ACTIONS = useMemo(
    () => [
      { ...ACCEPT, isDisabled: checkedItem.status !== PENDING },
      { ...REJECT, isDisabled: checkedItem.status !== PENDING },
      { ...COMPLETE, isDisabled: checkedItem.status !== SHIPPED },
      { ...SEND_RECEIPT, isDisabled: checkedItem.status === REJECTED },
    ],
    [checkedItem.status]
  );

  const onCheckItem = (id, status) => {
    if (id === checkedItem.id) {
      setCheckedItem({});
    } else {
      setCheckedItem({ id, status });
    }
  };

  const onClickOrder = (id) => {
    history.push(`${PATHS.orders}/${id}`);
  };

  return (
    <>
      <LoaderBar isVisible={displayLoader} />
      {!isLarge && (
        <div className="mb-6">
          <SearchInput
            className="w-full h-10"
            onChange={onSearch}
            value={searchTerm}
          />
          <div className="flex flex-row mt-2">
            <Dropdown
              className="flex-1 mr-2"
              placeholder="Filter by"
              options={ORDER_STATUS_OPTIONS}
              onChange={onChangeStatusFilter}
              value={statusFilter}
              isClearable
            />
            <DropdownWithIcon
              className="flex-1 mx-1"
              placeholder="Last 30 Days"
              options={ORDER_DATE_OPTIONS}
              onChange={onChangeDateFilter}
              value={dateFilter}
            />
            <ExportIcon className="h-6 w-4 ml-2 fill-current text-gray-400 my-auto cursor-pointer" />
          </div>
        </div>
      )}
      <div className="bg-white w-full lg:px-5 lg:min-w-max lg:w-full overflow-x-auto shadow-md lg:shadow-none border-transparent lg:border-0 lg:overflow-visible rounded py-5 lg:pt-10">
        {isLarge ? (
          <>
            <div className="flex items-center ml-4 lg:ml-0 lg:justify-between">
              <div className="flex flex-row">
                <Dropdown
                  className="flex-auto lg:flex-initial mr-2 lg:mr-4 lg:w-80"
                  placeholder="Select action"
                  options={ORDER_ACTIONS}
                  onApply={onApplyAction}
                  isDisabled={!checkedItem.id}
                  onChange={(option) => setSelectedAction(option)}
                  value={selectedAction}
                  testId="orderActions"
                />
                <SearchInput onChange={onSearch} value={searchTerm} />
              </div>
              <div className="flex flex-row">
                <Dropdown
                  className="lg:w-52 lg:mr-4"
                  placeholder="Filter by"
                  options={ORDER_STATUS_OPTIONS}
                  onChange={onChangeStatusFilter}
                  value={statusFilter}
                  isClearable
                  testId="orderStatus"
                />
                <DropdownWithIcon
                  placeholder="Last 30 Days"
                  options={ORDER_DATE_OPTIONS}
                  onChange={onChangeDateFilter}
                  value={dateFilter}
                />
                <ExportModal
                  onConfirm={onConfirmExport}
                  title={TEXT.exportHeader}
                  buttonClass="bg-white rounded border border-brand-primary text-brand-primary lg:w-44 flex-shrink-0"
                />
              </div>
            </div>
            <table className="table-auto w-full min-w-max lg:mt-6">
              <thead className="hidden lg:table-header-group lg:border-t lg:border-b lg:border-javelin-gray-border">
                <tr>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="table-light-text text-opacity-60 table-data font-normal py-6"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white py-6">
                {data.map((item, idx) => (
                  <tr
                    data-testid="table-data"
                    key={idx}
                    className="hover:bg-gray-50 cursor-pointer py-4"
                    onClick={() => onClickOrder(item.id)}
                  >
                    <td className="py-3 pl-2 pr-6">
                      <Checkbox
                        name={item.id}
                        checked={checkedItem.id === item.id}
                        readOnly
                        onChange={() => onCheckItem(item.id, item.status)}
                      />
                    </td>
                    <td className="table-data">
                      <p className="table-light-text">{item.id}</p>
                    </td>
                    <td className="table-data">
                      <p className="table-light-text">{item.shopper_email}</p>
                    </td>
                    <td className="table-data">
                      <p className="table-light-text">
                        {item.shopper_phone_number}
                      </p>
                    </td>
                    <td className="table-data">
                      <p className="table-light-text">{`${item.currency_symbol}${item.total}`}</p>
                    </td>
                    <td className="table-data">
                      <p className="green-label">{item.status}</p>
                    </td>
                    <td className="table-data text-left">
                      <p className="table-light-text inline-block mr-2">
                        {format(new Date(item.updated_at), 'd MMM')}
                      </p>
                      <ReceiptIcon className="fill-current text-javelin-gray-light w-3 inline-block" />
                    </td>
                    <td className="table-data">
                      <p className="table-light-text">
                        {format(new Date(item.created_at), 'd MMM')}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <Dropdown
              className="flex-auto mx-4"
              placeholder="Select action"
              options={ORDER_ACTIONS}
              onApply={onApplyAction}
              isDisabled={!checkedItem.id}
              onChange={(option) => setSelectedAction(option)}
              value={selectedAction}
              testId="orderActions"
            />
            <div className="flex flex-col w-full overflow-x-hidden mt-4">
              {data.map((item, idx) => (
                <div
                  key={idx}
                  className="px-4 flex w-full items-start border-t border-gray-100 py-5 cursor-pointer"
                  data-testid="table-data"
                  onClick={(e) => onClickOrder(item.id)}
                >
                  <Checkbox
                    name={item.id}
                    checked={checkedItem.id === item.id}
                    readOnly
                    onChange={() => onCheckItem(item.id, item.status)}
                  />
                  <div className="justify-around flex-auto ml-2">
                    <p className="text-tiny text-gray-400">{item.id}</p>
                    <p className="table-light-text leading-5">
                      {item.shopper_email}
                    </p>
                    <p className="table-light-text leading-5">
                      {item.shopper_phone_number}
                    </p>
                  </div>
                  <div className="flex-end">
                    <p className="green-label text-right">{item.status}</p>
                    <p className="table-light-text mt-1 text-right">{`${item.currency_symbol}${item.total}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!!data.length && <hr className="horizontal-line mt-0" />}
        <div className="mr-6 lg:mr-0">
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
};

OrderTable.propTypes = {
  headers: arrayOf(oneOfType([string, object])),
  data: arrayOf(shape({})),
};
OrderTable.defaultProps = {
  headers: [
    <></>,
    TEXT.orderId,
    TEXT.email,
    TEXT.phone,
    TEXT.total,
    TEXT.status,
    TEXT.date,
    TEXT.sent,
  ],
  data: [],
};
export default memo(OrderTable);
